import React, { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import { useAuth } from '@/features/auth/hooks/useAuth'

// Lands here after /login redirects to Google and Google's own redirect
// bounces back through the API's /auth/google/callback, which hands this
// page a one-time code (see apps/api's handoff-repository.ts for why it's a
// code and not the session itself). This page's only job is to trade that
// code for a session and continue on; errors go back to /login so there's
// one sign-in form, not two.
const GoogleCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { currentTheme } = useTheme()
  const { loginWithGoogleCode } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const ranRef = useRef(false)

  useEffect(() => {
    // StrictMode double-invokes effects in dev; the code is single-use, so a
    // second exchange would just fail — guard against running it twice.
    if (ranRef.current) return
    ranRef.current = true

    const code = searchParams.get('code')
    if (!code) {
      navigate('/login?googleAuth=error&reason=missing_params', { replace: true })
      return
    }

    loginWithGoogleCode(code).catch((err) => {
      // The session exchange (POST /auth/google/session) throws an AppError
      // whose message is already written for the person signing in (see
      // auth/google/service.ts), unlike the callback redirect's `reason` code
      // below — so it's passed through as-is rather than mapped through
      // googleSignInErrorCopy.
      const message = err instanceof Error ? err.message : 'Something went wrong signing in with Google. Please try again.'
      setError(message)
      navigate('/login', { replace: true, state: { googleSignInError: message } })
    })
  }, [searchParams, navigate, loginWithGoogleCode])

  return (
    <div
      className="flex min-h-screen items-center justify-center transition-colors duration-300"
      style={{ backgroundColor: currentTheme.primary }}
    >
      {!error && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: currentTheme.text }} />
          <p className="text-sm" style={{ color: currentTheme.secondrytext }}>
            Finishing sign-in with Google…
          </p>
        </div>
      )}
    </div>
  )
}

export default GoogleCallbackPage
